import React from "react";
import {
  Table,
  Header,
  TableProps,
  Popover,
  Button,
  KeyValuePairs,
  KeyValuePairsProps,
  Link,
} from "@amzn/awsui-components-console";
import { FixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";
import { NumericValue } from "@smithy/core/serde";
import { Domain } from "src/config/BartLambdaConfig";
import Decimal from "decimal.js";
import { getTransactionInfo } from "src/pages/commons/transaction-info";

interface Props {
  fixedFeeConfig: FixedFeeConfig;
  domain: Domain;
}

const DUMMY_COLUMN_DEFINITION = {
  id: "",
  header: "",
  cell: () => "",
};

// Helper function to format numeric values
const formatNumber = (value: any) => {
  if (!value) return "-";
  return Number(
    value instanceof NumericValue ? value.string : value.toString(),
  ).toLocaleString("en-US", {
    maximumFractionDigits: 4,
  });
};

export const AdjustmentsTable: React.FC<Props> = ({
  fixedFeeConfig,
  domain,
}) => {
  if (
    !fixedFeeConfig.adjustments ||
    Object.keys(fixedFeeConfig.adjustments).length === 0
  ) {
    return null;
  }

  // Helper to format colored amounts
  const formatColoredAmount = (value: any, label?: string) => {
    if (!value) return "-";
    const numValue =
      typeof value === "object" && value.string
        ? Number(value.string)
        : Number(value);

    if (numValue === 0) return "0";

    const color = numValue > 0 ? "#00802f" : "#db0000";
    const displayValue = Math.abs(numValue).toLocaleString("en-US");

    return (
      <span style={{ color }}>
        {displayValue}
        {label && ` (${label})`}
      </span>
    );
  };

  // Helper to get transaction items for KeyValuePairs
  const getTransactionItems = (externalId: string, transactionType: string) => {
    const transactionInfo = getTransactionInfo(
      externalId,
      transactionType,
      fixedFeeConfig.environment === "TEST",
      domain,
    );
    return [
      {
        label: "Transaction(s)",
        value: (
          <Link external href={`/transactionsearch/external_id/${externalId}`}>
            {externalId}
          </Link>
        ),
      },
      {
        label: "FuseBillingService Workflows",
        value: (
          <span>
            <Link external href={transactionInfo.fuse.transactionWf?.link}>
              Transaction WF
            </Link>
            ,&nbsp;
            <Link external href={transactionInfo.fuse.fusenWf.link}>
              FUSEN WF
            </Link>
          </span>
        ),
      },
      {
        label: "Other Links",
        value: (
          <span>
            <Link external href={transactionInfo.acid.link}>
              ACID
            </Link>
            ,&nbsp;
            <Link external href={transactionInfo.flash.link}>
              FLASH
            </Link>
          </span>
        ),
      },
    ];
  };

  // Helper to get issuance transaction links popover
  const getIssuanceTransactionPopover = () => {
    const issuanceExternalId = `${fixedFeeConfig.fixedFeeId}.Payment`;
    const groups: KeyValuePairsProps.Group[] = [
      {
        type: "group",
        title: "Issuance Transaction",
        items: getTransactionItems(issuanceExternalId, "CHARGE"),
      },
    ];

    return (
      <Popover
        renderWithPortal
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={<KeyValuePairs columns={1} items={groups} />}
      >
        <Button variant="inline-link">Expand</Button>
      </Popover>
    );
  };

  // Helper to get adjustment transaction links popover
  const getAdjustmentTransactionPopover = (
    item: any,
    executionIndex: number,
  ) => {
    if (
      !item.adjustmentAmount &&
      !item.receivableShiftAmount &&
      !item.deferralRevenueShiftAmount
    ) {
      return <span>-</span>;
    }

    const adjustmentAmount = item.adjustmentAmount
      ? typeof item.adjustmentAmount === "object" &&
        item.adjustmentAmount.string
        ? Number(item.adjustmentAmount.string)
        : Number(item.adjustmentAmount)
      : 0;

    const receivableShiftAmount = item.receivableShiftAmount
      ? typeof item.receivableShiftAmount === "object" &&
        item.receivableShiftAmount.string
        ? Number(item.receivableShiftAmount.string)
        : Number(item.receivableShiftAmount)
      : 0;

    const deferralRevenueShiftAmount = item.deferralRevenueShiftAmount
      ? typeof item.deferralRevenueShiftAmount === "object" &&
        item.deferralRevenueShiftAmount.string
        ? Number(item.deferralRevenueShiftAmount.string)
        : Number(item.deferralRevenueShiftAmount)
      : 0;

    const groups: KeyValuePairsProps.Group[] = [];

    // Adjustment transaction
    if (adjustmentAmount !== 0) {
      const isTrueUp = adjustmentAmount > 0;
      const adjustmentExternalId = `${fixedFeeConfig.fixedFeeId}-${executionIndex}.${isTrueUp ? "TrueUp" : "TrueDown"}`;

      groups.push({
        type: "group",
        title: "Adjustment Transaction",
        items: getTransactionItems(adjustmentExternalId, "TRANSACTION"),
      });
    }

    // SubBalanceShift transaction
    if (receivableShiftAmount !== 0 || deferralRevenueShiftAmount !== 0) {
      const subBalanceShiftExternalId = `${fixedFeeConfig.fixedFeeId}-${executionIndex}.SubBalShift`;

      groups.push({
        type: "group",
        title: "SubBalanceShift Transaction",
        items: getTransactionItems(subBalanceShiftExternalId, "CHARGE"),
      });
    }

    return (
      <Popover
        renderWithPortal
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={<KeyValuePairs columns={groups.length} items={groups} />}
      >
        <Button variant="inline-link">Expand</Button>
      </Popover>
    );
  };

  const adjustmentColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    DUMMY_COLUMN_DEFINITION,
    {
      id: "transaction",
      header: "Transaction Info",
      cell: (item: any) =>
        item.executionIndex !== undefined
          ? getAdjustmentTransactionPopover(item, item.executionIndex)
          : getIssuanceTransactionPopover(),
    },
    {
      id: "adjustmentDate",
      header: "Adjustment Date",
      cell: (item: any) => item.adjustmentDate,
    },
    {
      id: "adjustingPeriod",
      header: "Adjusting Period",
      cell: (item: any) =>
        item.periodStartDate
          ? `${item.periodStartDate || "-"} to ${item.periodEndDate || "-"}`
          : "-",
    },
    {
      id: "activeBenefitCount",
      header: "Active Benefit Count",
      cell: (item: any) =>
        item.activeBenefitCount !== undefined
          ? item.activeBenefitCount.toLocaleString("en-US")
          : "-",
    },
    {
      id: "deferredRevenue",
      header: "Deferred Revenue",
      cell: (item: any) => formatNumber(item.deferredRevenue),
    },
    {
      id: "revisedPlanPrice",
      header: "Revised Plan Price",
      cell: (item: any) => formatNumber(item.revisedPlanPrice),
    },
    {
      id: "adjustmentAmount",
      header: "Adjustment Amount",
      cell: (item: any) => {
        if (!item.adjustmentAmount) return "-";
        const numValue =
          typeof item.adjustmentAmount === "object" &&
          item.adjustmentAmount.string
            ? Number(item.adjustmentAmount.string)
            : Number(item.adjustmentAmount);
        if (numValue === 0) return "0";
        const label = numValue > 0 ? "True-Up" : "True-Down";
        return formatColoredAmount(item.adjustmentAmount, label);
      },
    },
  ];

  if (fixedFeeConfig.reclassBalance) {
    adjustmentColumnDefinitions.push(
      {
        id: "receivableShiftAmount",
        header: "Receivable Shift Amount",
        cell: (item: any) => formatColoredAmount(item.receivableShiftAmount),
      },
      {
        id: "deferralRevenueShiftAmount",
        header: "Deferral Revenue Shift Amount",
        cell: (item: any) =>
          formatColoredAmount(item.deferralRevenueShiftAmount),
      },
      {
        id: "longTermReceivables",
        header: "Long-term Receivables",
        cell: (item: any) => formatNumber(item.longTermReceivables),
      },
      {
        id: "longTermDeferredRevenue",
        header: "Long-term Deferred Revenue",
        cell: (item: any) => formatNumber(item.longTermDeferredRevenue),
      },
      {
        id: "unbilledAccountsReceivables",
        header: "Unbilled AR",
        cell: (item: any) => formatNumber(item.unbilledAccountsReceivables),
      },
      {
        id: "unearnedRevenue",
        header: "Unearned Revenue",
        cell: (item: any) => formatNumber(item.unearnedRevenue),
      },
    );
  }

  adjustmentColumnDefinitions.push(DUMMY_COLUMN_DEFINITION);

  // Get executed adjustments with their index
  const executedAdjustmentDates = Object.entries(fixedFeeConfig.adjustments)
    .filter(([_, adjustment]) => adjustment.adjustmentAmount !== undefined)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date], index) => ({ date, index: index + 1 }));

  // If reclass is enabled, calculate the LT/ST amount as iterated
  const adjustmentData = [];

  let longTermReceivables: Decimal | undefined,
    longTermDeferredRevenue: Decimal | undefined,
    unbilledAccountsReceivables: Decimal | undefined,
    unearnedRevenue: Decimal | undefined;

  if (fixedFeeConfig.reclassBalance) {
    longTermReceivables = new Decimal(
      fixedFeeConfig.reclassBalance.longTermReceivables?.string ?? "0",
    );
    longTermDeferredRevenue = new Decimal(
      fixedFeeConfig.reclassBalance.longTermDeferredRevenue?.string ?? "0",
    );
    unbilledAccountsReceivables = new Decimal(
      fixedFeeConfig.reclassBalance.unbilledAccountsReceivables?.string ?? "0",
    );
    unearnedRevenue = new Decimal(
      fixedFeeConfig.reclassBalance.unearnedRevenue?.string ?? "0",
    );
  }

  adjustmentData.push({
    adjustmentDate: "ISSUANCE",
    longTermReceivables,
    longTermDeferredRevenue,
    unbilledAccountsReceivables,
    unearnedRevenue,
  });

  adjustmentData.push(
    ...Object.entries(fixedFeeConfig.adjustments)
      .sort(([date, _], [date2, __]) => date.localeCompare(date2))
      .map(([date, adjustment]) => {
        const executed = executedAdjustmentDates.find((e) => e.date === date);
        const data: any = {
          adjustmentDate: date,
          periodStartDate: adjustment.periodStartDate,
          periodEndDate: adjustment.periodEndDate,
          activeBenefitCount: adjustment.activeBenefitCount,
          deferredRevenue: adjustment.deferredRevenue,
          revisedPlanPrice: adjustment.revisedPlanPrice,
          adjustmentAmount: adjustment.adjustmentAmount,
          receivableShiftAmount: adjustment.receivableShiftAmount,
          deferralRevenueShiftAmount: adjustment.deferralRevenueShiftAmount,
          executionIndex: executed ? executed.index : 0,
        };

        // only populate reclass relevant items if reclass is enabled and this occurrence has executed
        if (fixedFeeConfig.reclassBalance && adjustment.adjustmentAmount) {
          longTermReceivables = longTermReceivables!.sub(
            new Decimal(adjustment.receivableShiftAmount?.string ?? "0"),
          );
          longTermDeferredRevenue = longTermDeferredRevenue!.sub(
            new Decimal(adjustment.deferralRevenueShiftAmount?.string ?? "0"),
          );
          unbilledAccountsReceivables = unbilledAccountsReceivables!.add(
            new Decimal(adjustment.receivableShiftAmount?.string ?? "0"),
          );
          unearnedRevenue = unearnedRevenue!.add(
            new Decimal(adjustment.deferralRevenueShiftAmount?.string ?? "0"),
          );

          data.longTermReceivables = longTermReceivables;
          data.longTermDeferredRevenue = longTermDeferredRevenue;
          data.unbilledAccountsReceivables = unbilledAccountsReceivables;
          data.unearnedRevenue = unearnedRevenue;
        }

        return data;
      }),
  );

  return (
    <Table
      columnDefinitions={adjustmentColumnDefinitions}
      items={adjustmentData}
      wrapLines={false}
      resizableColumns={false}
      header={
        <Header
          counter={`(${Object.entries(fixedFeeConfig.adjustments).length})`}
        >
          Adjustments
        </Header>
      }
      stickyColumns={{ first: 4, last: 0 }}
    />
  );
};
